import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { ProspectusDataService } from './prospectus-data.service';

export enum ImplantType {
  SAWING = 'SAWING',
  TRANSPLANT = 'TRANSPLANT',
}

export enum ConductionType {
  INTEGRATED = 'INTEGRATED',
  ORGANIC = 'ORGANIC',
  BIODYNAMIC = 'BIODYNAMIC',
}

export enum CultivationTechnique {
  SOIL = 'SOIL',
  SOILLESS = 'SOILLESS',
}

export enum SoilClassification {
  SANDY = 'SANDY',
  SANDY_LOAM = 'SANDY_LOAM',
  SILTY = 'SILTY',
  LOAMY_SAND = 'LOAMY_SAND',
  LOAM = 'LOAM',
  SILTY_LOAM = 'SILTY_LOAM',
  SANDY_CLAY_LOAM = 'SANDY_CLAY_LOAM',
  CLAY_LOAM = 'CLAY_LOAM',
  SILTY_CLAY_LOAM = 'SILTY_CLAY_LOAM',
  SANDY_CLAY = 'SANDY_CLAY',
  SILTY_CLAY = 'SILTY_CLAY',
  CLAY = 'CLAY',
}

export enum SoillessSubstrate {
  ROCKWOOL = 'ROCKWOOL',
  COCO_FIBER = 'COCO_FIBER',
  PEAT = 'PEAT',
  FLOATING_SYSTEM = 'FLOATING_SYSTEM',
  NFT = 'NFT',
  AEROPONICS = 'AEROPONICS',
  AQUAPONICS = 'AQUAPONICS',
}

export enum CycleType {
  EARLY = 'EARLY',
  MEDIUM = 'MEDIUM',
  LATE = 'LATE',
}

export enum FarmCategoryType {
  PRODUCTION = 'PRODUCTION',
  NURSERY = 'NURSERY',
}

// unitOfMeasureProduction and unitOfMeasureTotalYield are omitted: not available in backend schema.
const PROSPECTUS_FIELDS = gql`
  fragment ProspectusFields on Prospectus {
    id
    serial
    company
    active
    notes
    lot
    identifier
    sheet
    particles
    municipality
    address
    extension
    gps
    district
    region
    country
    cooperative
    supplier
    taxCode
    layer
    area
    perimeter
    nodeInstalled
    companyManager
    companyManagerPhone
    companyManagerEmail
    technicalManager
    technicalManagerPhone
    technicalManagerEmail
    certifications
    conductionType
    implantType
    transplantationDate
    qty
    variety {
      crop
      cultivar
      description
    }
    cultivationTechnique {
      technique
      soilTexture {
        clay
        silt
        sand
        skeleton
        classification
        analysisDate
      }
      soillessSubstrate {
        types
        date
        description
      }
    }
    farmCategory {
      category
      production {
        transplantationDate
        dailyTransplantHa
        plantsDensityHa
        germinationDate
        floweringStart
        fruitingStart
        vegetativeEnd
        maturationStart
        dryingStart
        cycle
        cycleDays
        unitYieldKgM2
        cycleEndDate
        tonsPerTruck
        logisticsNotes
        destinationPlant
      }
      nursery {
        sawingNumber
        sawingDay
        seedsLotNumber
        sawedPlants
        germinatedPlants
        germinationDate
        cycleEndDate
        presumedHarvestDate
        presumedDeliveryDate
      }
    }
    map {
      lat
      lng
    }
    closedAt
    createdAt
    updatedAt
  }
`;

const PROSPECTUS_MONITORING_FIELDS = gql`
  fragment ProspectusMonitoringFields on Prospectus {
    id
    serial
    company
    active
    lot
    sheet
    particles
    extension
    district
    region
    cooperative
    supplier
    nodeInstalled
    variety {
      crop
      cultivar
      description
    }
    cultivationTechnique {
      technique
    }
    farmCategory {
      category
      production {
        transplantationDate
        cycle
        cycleDays
        unitYieldKgM2
        tonsPerTruck
        logisticsNotes
        destinationPlant
      }
      nursery {
        sawingNumber
        sawingDay
        sawedPlants
        germinatedPlants
        presumedHarvestDate
        presumedDeliveryDate
      }
    }
  }
`;

@Injectable()
export class ProspectusService extends ProspectusDataService {
  constructor(private readonly apollo: Apollo) {
    super();
  }

  getProspectusForMonitoring(variables: any): {
    query: any;
    variables: any;
  } {
    return {
      query: this.apollo.use('prospectus').watchQuery({
        query: gql`
          query prospectuses($serial: String, $active: Boolean, $limit: Int, $offset: Int, $category: FarmCategoryType) {
            prospectuses(active: $active, serial: $serial, limit: $limit, offset: $offset, category: $category) {
              ...ProspectusMonitoringFields
            }
          }
          ${PROSPECTUS_MONITORING_FIELDS}
        `,
        variables: variables,
        fetchPolicy: 'no-cache',
      }),
      variables: variables,
    };
  }

  getAllProspectuses(variables: any): {
    query: any;
    variables: any;
  } {
    return {
      query: this.apollo.use('prospectus').watchQuery({
        query: gql`
          query prospectuses($serial: String, $active: Boolean, $limit: Int, $offset: Int, $category: FarmCategoryType) {
            prospectuses(active: $active, serial: $serial, limit: $limit, offset: $offset, category: $category) {
              ...ProspectusFields
            }
          }
          ${PROSPECTUS_FIELDS}
        `,
        variables: variables,
        fetchPolicy: 'no-cache',
      }),
      variables: variables,
    };
  }

  addProspectus(variables: any): {
    mutation: any;
    variables: any;
  } {
    return {
      mutation: this.apollo.use('prospectus').mutate({
        mutation: gql`
          mutation addProspectus($input: AddProspectusInput!) {
            addProspectus(input: $input) {
              ...ProspectusFields
            }
          }
          ${PROSPECTUS_FIELDS}
        `,
        variables: variables,
        fetchPolicy: 'no-cache',
      }),
      variables: variables,
    };
  }

  updateProspectus(variables: any): {
    mutation: any;
    variables: any;
  } {
    return {
      mutation: this.apollo.use('prospectus').mutate({
        mutation: gql`
          mutation updateProspectus(
            $prospectusId: ID!
            $input: UpdateProspectusInput!
          ) {
            updateProspectus(prospectusId: $prospectusId, input: $input) {
              ...ProspectusFields
            }
          }
          ${PROSPECTUS_FIELDS}
        `,
        variables: variables,
        fetchPolicy: 'no-cache',
      }),
      variables: variables,
    };
  }

  closeProspectus(variables: any): {
    mutation: any;
    variables: any;
  } {
    return {
      mutation: this.apollo.use('prospectus').mutate({
        mutation: gql`
          mutation closeProspectus($prospectusId: ID!) {
            closeProspectus(prospectusId: $prospectusId)
          }
        `,
        variables: variables,
        fetchPolicy: 'no-cache',
      }),
      variables: variables,
    };
  }

  openProspectus(variables: any): {
    mutation: any;
    variables: any;
  } {
    return {
      mutation: this.apollo.use('prospectus').mutate({
        mutation: gql`
          mutation openProspectus($prospectusId: ID!) {
            openProspectus(prospectusId: $prospectusId)
          }
        `,
        variables: variables,
        fetchPolicy: 'no-cache',
      }),
      variables: variables,
    };
  }

  getCrops(): {
    query: any;
  } {
    return {
      query: this.apollo.use('prospectus').watchQuery({
        query: gql`
          query crops {
            crops
          }
        `,
        fetchPolicy: 'no-cache',
      }),
    };
  }

  getVarieties(variables: any): {
    query: any;
    variables: any;
  } {
    return {
      query: this.apollo.use('prospectus').watchQuery({
        query: gql`
          query varieties($crop: String!, $cultivar: String, $description: String) {
            varieties(crop: $crop, cultivar: $cultivar, description: $description) {
              crop
              cultivar
              description
            }
          }
        `,
        variables: variables,
        fetchPolicy: 'no-cache',
      }),
      variables: variables,
    };
  }

  setProspectusVariety(variables: any): {
    mutation: any;
    variables: any;
  } {
    return {
      mutation: this.apollo.use('prospectus').mutate({
        mutation: gql`
          mutation setProspectusVariety(
            $prospectusId: ID!
            $crop: String
            $cultivar: String
            $description: String
          ) {
            setProspectusVariety(
              prospectusId: $prospectusId
              crop: $crop
              cultivar: $cultivar
              description: $description
            )
          }
        `,
        variables: variables,
        fetchPolicy: 'no-cache',
      }),
      variables: variables,
    };
  }

  setFarmProductionCategory(variables: any): {
    mutation: any;
    variables: any;
  } {
    return {
      mutation: this.apollo.use('prospectus').mutate({
        mutation: gql`
          mutation setFarmProductionCategory(
            $prospectusId: ID!
            $input: SetFarmProductionCategoryInput!
          ) {
            setFarmProductionCategory(prospectusId: $prospectusId, input: $input) {
              category
              production {
                transplantationDate
                dailyTransplantHa
                plantsDensityHa
                germinationDate
                floweringStart
                fruitingStart
                vegetativeEnd
                maturationStart
                dryingStart
                cycle
                cycleDays
                unitYieldKgM2
                cycleEndDate
                tonsPerTruck
                logisticsNotes
                destinationPlant
              }
            }
          }
        `,
        variables: variables,
        fetchPolicy: 'no-cache',
      }),
      variables: variables,
    };
  }

  setFarmNurseryCategory(variables: any): {
    mutation: any;
    variables: any;
  } {
    return {
      mutation: this.apollo.use('prospectus').mutate({
        mutation: gql`
          mutation setFarmNurseryCategory(
            $prospectusId: ID!
            $input: SetFarmNurseryCategoryInput!
          ) {
            setFarmNurseryCategory(prospectusId: $prospectusId, input: $input) {
              category
              nursery {
                sawingNumber
                sawingDay
                seedsLotNumber
                sawedPlants
                germinatedPlants
                germinationDate
                cycleEndDate
                presumedHarvestDate
                presumedDeliveryDate
              }
            }
          }
        `,
        variables: variables,
        fetchPolicy: 'no-cache',
      }),
      variables: variables,
    };
  }

  getHarvests(variables: any): {
    query: any;
    variables: any;
  } {
    return {
      query: this.apollo.use('prospectus').watchQuery({
        query: gql`
          query harvests($id: ID!, $limit: Int, $offset: Int) {
            harvests(prospectusId: $id, limit: $limit, offset: $offset) {
              id
              client
              presumedCollectionDate
              presumedQuantityCollected
              inspectionDate
              presumedTotalQuantityCollected
              realCollectionDate
              realQuantityCollected
            }
          }
        `,
        variables: variables,
        fetchPolicy: 'no-cache',
      }),
      variables: variables,
    };
  }

  addHarvests(variables: any): {
    mutation: any;
    variables: any;
  } {
    return {
      mutation: this.apollo.use('prospectus').mutate({
        mutation: gql`
          mutation addHarvests($input: AddHarvestsInput!, $prospectusId: ID!) {
            addHarvests(input: $input, prospectusId: $prospectusId)
          }
        `,
        variables: variables,
        fetchPolicy: 'no-cache',
      }),
      variables: variables,
    };
  }

  deleteProspectusHeavests(variables: any): {
    mutation: any;
    variables: any;
  } {
    return {
      mutation: this.apollo.use('prospectus').mutate({
        mutation: gql`
          mutation deleteHarvests($ids: [ID!]!) {
            deleteHarvests(ids: $ids)
          }
        `,
        variables: variables,
        fetchPolicy: 'no-cache',
      }),
      variables: variables,
    };
  }

  addProspectusMap(variables: any): {
    mutation: any;
    variables: any;
  } {
    return {
      mutation: this.apollo.use('prospectus').mutate({
        mutation: gql`
          mutation addProspectusMap($prospectusId: ID!, $map: [PointInput!]!) {
            addProspectusMap(prospectusId: $prospectusId, map: $map) {
              lat
              lng
            }
          }
        `,
        variables: variables,
        fetchPolicy: 'no-cache',
      }),
      variables: variables,
    };
  }

  deleteProspectusMap(variables: any): {
    mutation: any;
    variables: any;
  } {
    return {
      mutation: this.apollo.use('prospectus').mutate({
        mutation: gql`
          mutation deleteProspectusMap($prospectusId: ID!) {
            deleteProspectusMap(prospectusId: $prospectusId)
          }
        `,
        variables: variables,
        fetchPolicy: 'no-cache',
      }),
      variables: variables,
    };
  }

  setSoilTextureTechnique(variables: any) {
    return this.apollo.use('prospectus').mutate({
      mutation: gql`
        mutation setSoilTextureTechnique($prospectusId: ID!, $input: SetSoilTextureTechniqueInput!) {
          setSoilTextureTechnique(prospectusId: $prospectusId, input: $input) {
            technique
            soilTexture {
              clay
              silt
              sand
              skeleton
              classification
              analysisDate
            }
          }
        }
      `,
      variables,
      fetchPolicy: 'no-cache'
    });
  }

  setSoillessSubstrateTechnique(variables: any) {
    return this.apollo.use('prospectus').mutate({
      mutation: gql`
        mutation setSoillessSubstrateTechnique($prospectusId: ID!, $input: SetSoillessSubstrateTechniqueInput!) {
          setSoillessSubstrateTechnique(prospectusId: $prospectusId, input: $input) {
            technique
            soillessSubstrate {
              types
              description
              date
            }
          }
        }
      `,
      variables,
      fetchPolicy: 'no-cache'
    });
  }

  getSoilTextureClassifications() {
    return this.apollo.use('prospectus').watchQuery({
      query: gql`
        query getSoilTextureClassifications {
          soilTextureClassifications
        }
      `,
      fetchPolicy: 'cache-first'
    });
  }

  getSoillessSubstrateTypes() {
    return this.apollo.use('prospectus').watchQuery({
      query: gql`
        query getSoillessSubstrateTypes {
          soillessSubstrateTypes
        }
      `,
      fetchPolicy: 'cache-first'
    });
  }

  getDestinationPlants() {
    return this.apollo.use('prospectus').watchQuery<{ destinationPlants: string[] }>({
      query: gql`
        query DestinationPlants {
          destinationPlants
        }
      `,
      fetchPolicy: 'cache-first'
    });
  }

  getTotalExtensionByCompany(company: string) {
    return this.apollo.use('prospectus').watchQuery<{ totalExtensionByCompany: string }>({
      query: gql`
        query TotalExtensionByCompany($company: String!) {
          totalExtensionByCompany(company: $company)
        }
      `,
      variables: { company },
      fetchPolicy: 'no-cache',
    });
  }

  getMonitoringProd(variables: { serials: string[]; limit?: number; offset?: number }): {
    query: any;
    variables: any;
  } {
    return {
      query: this.apollo.use('prospectus').watchQuery({
        query: gql`
          query monitoringProd($serials: [String!]!, $limit: Int, $offset: Int) {
            monitoringProd(serials: $serials, limit: $limit, offset: $offset) {
              serial
              id
              company
              cooperative
              supplier
              nodeInstalled
              lot
              identifier
              extension
              region
              district
              municipality
              sheet
              particles
              variety
              technique
              conductionType
              plantName
              transplantationDate
              cycle
              cycleDays
              unitYieldKgM2
              inspectionDate
              presumedCollectionDate
              presumedQuantityCollected
              presumedTotalQuantityCollected
              realCollectionDate
              realQuantityCollected
              client
              dailyTransplantHa
              tonsPerTruck
              logisticsNotes
              notes
            }
          }
        `,
        variables,
        fetchPolicy: 'no-cache',
      }),
      variables,
    };
  }

  getMonitoringNursery(variables: { serials: string[]; limit?: number; offset?: number }): {
    query: any;
    variables: any;
  } {
    return {
      query: this.apollo.use('prospectus').watchQuery({
        query: gql`
          query monitoringNursery($serials: [String!]!, $limit: Int, $offset: Int) {
            monitoringNursery(serials: $serials, limit: $limit, offset: $offset) {
              serial
              id
              company
              cooperative
              supplier
              nodeInstalled
              lot
              identifier
              extension
              region
              district
              municipality
              sheet
              particles
              variety
              sawingNumber
              sawingDay
              sawedPlants
              germinatedPlants
              presumedHarvestDate
              presumedDeliveryDate
            }
          }
        `,
        variables,
        fetchPolicy: 'no-cache',
      }),
      variables,
    };
  }

  getProspectusesGps(variables: any): { query: any; variables: any } {
    return {
      query: this.apollo.use('prospectus').watchQuery({
        query: gql`
          query prospectusesGps($serial: String, $active: Boolean, $limit: Int) {
            prospectuses(serial: $serial, active: $active, limit: $limit) {
              serial
              gps
              createdAt
            }
          }
        `,
        variables: { ...variables, limit: 1 },
        fetchPolicy: 'no-cache',
      }),
      variables,
    };
  }
}
